import { useEffect, useState } from 'react';
import type { IEmbloomQuestionnaire } from '../../../../interfaces/embloomQuestionnaire';
import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces/customControlDiagnosticReportReadProps';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { removeHtmlTags } from '../../../../../../common/utils/removeHtmlTags';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { DiagnosticReportReadButtons } from '../../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { RequestReplicateGgzoButton } from '../../../../elements/carePathButtons/RequestReplicateGgzoButton';
import { FormDivider, FormParagraph, InfoTooltip, ListHeader } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { FormSubHeader } from '../../../../elements/forms/formSection/FormSection';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InlineReadElement } from '../../../../elements/inlineReadElement/InlineReadElement';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { GgzoEmbl1DiagnosticReportSummary } from './GgzoEmblV1DiagnosticReportSummary';
import { Indications } from './Indications';

interface IScore {
    label: string;
    qualification?: string;
    score: string;
}

export function GgzoEmblV1DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    const [questionnaire, setQuestionnaire] = useState<IEmbloomQuestionnaire | undefined>();
    const scores = props.diagnosticReport.customControlData.scores as IScore[];
    const showQualificationsTooltip = scores.some((item: IScore) => !item.qualification);

    useEffect(() => {
        if (props.request.customControlData && props.request.customControlData.vragenlijst) {
            loadQuestionnaire(props.request.customControlData.vragenlijst);
        }
    }, []);

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || !!props.itemState.initialIsOpen,
                    })
                }
                organizationName={props.diagnosticReport?.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.findings')}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <GgzoEmbl1DiagnosticReportSummary {...props} />

                {scores.length > 0 && (
                    <>
                        <FormDivider />
                        <FormSubHeader>Scores</FormSubHeader>
                        <HorizontalFormGroupRead>
                            <div className="col-md-6 offset-md-6">
                                <ListHeader className="small-text">Score</ListHeader>
                            </div>
                            <div className="col-md-6">
                                <HorizontalFormGroupRead>
                                    <ListHeader className="small-text">Kwalificatie</ListHeader>
                                    {showQualificationsTooltip && (
                                        <InfoTooltip
                                            text="Dan is er voor de score geen normgroep beschikbaar óf is de score tijdens de grading niet vergeleken met een normgroep."
                                            tooltipHeader="Kwalificatie niet beschikbaar?"
                                        />
                                    )}
                                </HorizontalFormGroupRead>
                            </div>
                        </HorizontalFormGroupRead>
                        {scores.map((item) => (
                            <HorizontalFormGroupRead
                                key={item.label}
                                noMargin
                            >
                                <FormLabel>{removeHtmlTags(item.label)}</FormLabel>
                                <div className="col-md-6">
                                    <FormParagraph bold>{item.score}</FormParagraph>
                                </div>
                                {item.qualification && (
                                    <div className="col-md-6">
                                        <FormParagraph bold>{item.qualification}</FormParagraph>
                                    </div>
                                )}
                                {!item.qualification && (
                                    <div className="col-md-6">
                                        <InlineReadElement color="muted">Niet beschikbaar</InlineReadElement>
                                    </div>
                                )}
                            </HorizontalFormGroupRead>
                        ))}
                    </>
                )}

                <Indications indications={props.diagnosticReport.customControlData?.indications} />

                {props.diagnosticReport.customControlData.contactSummary && (
                    <>
                        <FormDivider />
                        <HorizontalFormGroupRead>
                            <FormLabel>Aantekeningen (verslag)</FormLabel>
                            <InputContainer>
                                <FormParagraph>{props.diagnosticReport.customControlData.contactSummary}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    </>
                )}

                <DiagnosticReportReadButtons
                    {...props}
                    customButtonsLeft={[
                        <RequestReplicateGgzoButton
                            {...props}
                            questionnaireName={questionnaire ? questionnaire.label : ''}
                        />,
                    ]}
                />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideExaminationReport')}
                    labelMore={translateKey('diagnosticReport.showExaminationReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );

    async function loadQuestionnaire(questionnaireId: string) {
        const result = await getJson(`/api/embloomQuestionnaires/${questionnaireId}`);
        setQuestionnaire(result.json);
    }
}
